import React, { useState, useEffect } from 'react';
import { getHrisKontrak, getHrisKontrakById, createHrisKontrak, updateHrisKontrak, deleteHrisKontrak, getKaryawan } from '../../api';
import API from '../../api';
import { Plus, Edit2, Trash2, FileText, Search, Upload, Download, Calendar, X, Eye, ExternalLink } from 'lucide-react';

const JENIS_KONTRAK = ['Tetap', 'Kontrak', 'PKL', 'Harian'];
const STATUS_KONTRAK = ['Aktif', 'Kadaluwarsa', 'Perpanjangan', 'Putus'];

export default function HrisKontrak() {
  const [data, setData] = useState([]);
  const [karyawan, setKaryawan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ karyawan_id: '', jenis_kontrak: 'Kontrak', tgl_mulai: '', tgl_selesai: '', no_kontrak: '', keterangan: '' });
  const [fileData, setFileData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileUploading, setFileUploading] = useState(false);
  const [fileKontrak, setFileKontrak] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [previewItem, setPreviewItem] = useState(null);

  const fetchData = () => {
    setLoading(true);
    Promise.all([getHrisKontrak(), getKaryawan()])
      .then(([dRes, kRes]) => { setData(dRes.data); setKaryawan(kRes.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ karyawan_id: '', jenis_kontrak: 'Kontrak', tgl_mulai: '', tgl_selesai: '', no_kontrak: '', keterangan: '' });
    setFileData(null);
    setFileName('');
    setFileKontrak('');
    setShowModal(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await getHrisKontrakById(id);
      const d = res.data;
      setEditing(d);
      setForm({
        karyawan_id: d.karyawan_id,
        jenis_kontrak: d.jenis_kontrak,
        tgl_mulai: d.tgl_mulai,
        tgl_selesai: d.tgl_selesai || '',
        status: d.status,
        no_kontrak: d.no_kontrak,
        keterangan: d.keterangan,
      });
      setFileKontrak(d.file_kontrak || '');
      setFileData(null);
      setFileName('');
      setShowModal(true);
    } catch (e) { alert('Gagal memuat data: ' + e.message); }
  };

  const handleSubmit = async () => {
    if (!form.karyawan_id || !form.tgl_mulai) { alert('Karyawan dan tanggal mulai harus diisi'); return; }
    try {
      let file_kontrak = fileKontrak;
      // Upload file if selected
      if (fileData) {
        setFileUploading(true);
        const formData = new FormData();
        formData.append('file', fileData);
        formData.append('id', editing ? editing.id : '0');
        const uploadRes = await API.post('/hris/kontrak/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        file_kontrak = uploadRes.data.file_path || uploadRes.data.filename || '';
        setFileUploading(false);
      }
      const payload = { ...form, file_kontrak };
      if (editing) {
        await updateHrisKontrak(editing.id, { ...payload, status: form.status || 'Aktif' });
      } else {
        await createHrisKontrak(payload);
      }
      setShowModal(false);
      fetchData();
    } catch (e) { alert('Gagal menyimpan: ' + (e.response?.data?.error || e.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus kontrak ini?')) return;
    try { await deleteHrisKontrak(id); fetchData(); }
    catch (e) { alert('Gagal menghapus'); }
  };

  const totalKontrak = data.length;
  const totalAktif = data.filter(d => d.status === 'Aktif').length;
  const totalKadaluwarsa = data.filter(d => d.status === 'Kadaluwarsa').length;
  const totalPerpanjangan = data.filter(d => d.status === 'Perpanjangan' || d.status === 'Putus').length;

  const filtered = data.filter(d => {
    const nama = d.nama || d.nama_karyawan || '';
    const matchSearch = !search || nama.toLowerCase().includes(search.toLowerCase()) || (d.no_kontrak || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || d.status === filterStatus;
    return matchSearch && matchStatus;
  });

  // Helper to extract filename from path (handles both old format with full path and new format)
  const getFileName = (path) => {
    if (!path) return '';
    const parts = path.replace(/\\/g, '/').split('/');
    return parts[parts.length - 1];
  };

  const getFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return '/uploads/kontrak/' + getFileName(path);
  };

  const getProgress = (mulai, selesai) => {
    if (!selesai) return null;
    const start = new Date(mulai);
    const end = new Date(selesai);
    const now = new Date();
    if (now < start) return 0;
    const total = end - start;
    if (total <= 0) return 100;
    const elapsed = now - start;
    const pct = Math.min(100, Math.max(0, (elapsed / total) * 100));
    return Math.round(pct);
  };

  const getSisaHari = (selesai) => {
    if (!selesai) return null;
    const end = new Date(selesai);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) return <div className="page-loading"><div className="spinner-lg" /></div>;

  return (
    <div className="page-container">
      <div className="page-header" style={{ borderBottom: '2px solid var(--gray-100)', paddingBottom: 16, marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--gray-600)' }}>
          <FileText size={22} style={{ marginRight: 8, verticalAlign: 'middle', color: 'var(--cyan-500)' }} />
          Data Kontrak Karyawan
        </h2>
      </div>

      {/* Stat Cards */}
      {data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid var(--info)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Total Kontrak</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gray-600)', lineHeight: 1.2 }}>{totalKontrak}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #2dce89' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Aktif</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#065f46', lineHeight: 1.2 }}>{totalAktif}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #fb6340' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Perpanjangan/Putus</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#92400e', lineHeight: 1.2 }}>{totalPerpanjangan}</div>
          </div>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 0 2rem 0 rgba(136,152,170,0.15)', borderLeft: '4px solid #f5365c' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: 4 }}>Kadaluwarsa</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 700, color: '#991b1b', lineHeight: 1.2 }}>{totalKadaluwarsa}</div>
          </div>
        </div>
      )}

      {/* Toolbar: Tambah | Search | Filter Status */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '12px 16px', background: '#fff', borderRadius: 12,
        boxShadow: '0 0 2rem 0 rgba(136,152,170,0.1)',
        flexWrap: 'wrap',
      }}>
        <button className="btn btn-primary" onClick={openCreate} style={{ whiteSpace: 'nowrap' }}>
          <Plus size={16} /> Tambah Data Kontrak
        </button>
        <div style={{ flex: 1, minWidth: 200, maxWidth: 320 }}>
          <div className="search-box" style={{ width: '100%', margin: 0 }}>
            <Search size={16} />
            <input placeholder="Cari berdasarkan nama atau no. kontrak..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <label style={{ fontSize: '0.78rem', color: 'var(--gray-400)', fontWeight: 500, whiteSpace: 'nowrap' }}>Status:</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="form-select"
            style={{ width: 'auto', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--gray-300)', fontSize: '0.82rem' }}>
            <option value="">Semua</option>
            {STATUS_KONTRAK.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
            <strong style={{ color: 'var(--gray-600)' }}>{filtered.length}</strong>/{totalKontrak}
          </span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="page-loading">Tidak ada data kontrak</div>
      ) : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))' }}>
          {filtered.map(d => {
            const progress = getProgress(d.tgl_mulai, d.tgl_selesai);
            const sisaHari = getSisaHari(d.tgl_selesai);
            const progressColor = progress !== null && progress >= 90 ? '#f5365c' : progress !== null && progress >= 70 ? '#fb6340' : '#2dce89';
            const statusStyle = d.status === 'Aktif'
              ? { bg: '#d1fae5', color: '#065f46', dot: '#2dce89' }
              : d.status === 'Kadaluwarsa'
                ? { bg: '#fee2e2', color: '#991b1b', dot: '#f5365c' }
                : { bg: '#fef3c7', color: '#92400e', dot: '#fb6340' };
            return (
              <div key={d.id} className="region-card" style={{ borderTop: `3px solid ${statusStyle.dot}` }}>
                {/* Card Header */}
                <div className="region-card-header" style={{
                  background: '#fff', borderBottom: '1px solid var(--gray-100)',
                  padding: '14px 16px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}>
                      <FileText size={20} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <h3 style={{ fontSize: '0.95rem', margin: 0, color: 'var(--gray-600)' }}>
                          {d.nama || d.nama_karyawan || '-'}
                        </h3>
                        <span style={{
                          fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4,
                          background: '#e6fafc', color: 'var(--cyan-700)', fontWeight: 500,
                        }}>{d.jenis_kontrak}</span>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                          background: statusStyle.bg, color: statusStyle.color,
                        }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%',
                            background: statusStyle.dot,
                          }} />
                          {d.status}
                        </span>
                      </div>
                      {d.no_kontrak && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 2 }}>
                          No. Kontrak: {d.no_kontrak}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Card Body */}
                <div className="region-card-body" style={{ padding: '12px 16px' }}>
                  <div className="region-detail">
                    <Calendar size={14} className="text-muted" />
                    <strong>Mulai:</strong> {d.tgl_mulai}
                  </div>
                  <div className="region-detail">
                    <Calendar size={14} className="text-muted" />
                    <strong>Selesai:</strong> {d.tgl_selesai || <span style={{ color: 'var(--gray-400)', fontStyle: 'italic' }}>Belum ditentukan</span>}
                  </div>
                  {progress !== null && (
                    <div className="region-detail" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem' }}>
                        <strong style={{ color: 'var(--gray-600)' }}>Progres</strong>
                        <span>
                          <span style={{ color: progressColor, fontWeight: 700 }}>{progress}%</span>
                          {sisaHari !== null && (
                            <span style={{ color: 'var(--gray-400)', marginLeft: 8 }}>
                              {sisaHari <= 0 ? '✕ Lewat' : `${sisaHari} hari lagi`}
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{
                        width: '100%', height: 6, background: 'var(--gray-100)',
                        borderRadius: 3, overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${Math.min(100, progress)}%`, height: '100%',
                          borderRadius: 3, background: progressColor,
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                    </div>
                  )}
                  {d.keterangan && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 8, marginTop: 8 }}>
                      <FileText size={14} className="text-muted" />
                      <span style={{ color: 'var(--gray-500)', fontSize: '0.8rem', fontStyle: 'italic' }}>
                        {d.keterangan}
                      </span>
                    </div>
                  )}
                  {d.file_kontrak && (
                    <div className="region-detail" style={{ borderTop: '1px solid var(--gray-100)', paddingTop: 10, marginTop: 8 }}>
                      <FileText size={14} style={{ color: 'var(--cyan-500)' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginBottom: 2 }}>File Kontrak</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: '0.78rem', color: 'var(--gray-600)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            maxWidth: 180,
                          }}>
                            {getFileName(d.file_kontrak)}
                          </span>
                          <a href={getFileUrl(d.file_kontrak)}
                             target="_blank" rel="noopener noreferrer"
                             className="btn btn-sm btn-ghost"
                             style={{ fontSize: '0.7rem', padding: '3px 10px', color: 'var(--cyan-500)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                             title="Download file">
                            <Download size={12} /> Download
                          </a>
                          <button className="btn-icon"
                             onClick={() => setPreviewItem(d)}
                             title="Preview file"
                             style={{ width: 28, height: 28, color: 'var(--cyan-500)' }}>
                            <Eye size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Card Actions */}
                <div className="action-btns" style={{
                  padding: '10px 16px', borderTop: '1px solid var(--gray-100)',
                  justifyContent: 'flex-end',
                }}>
                  <button className="btn-icon" title="Edit" onClick={() => openEdit(d.id)}><Edit2 size={14} /></button>
                  <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{
              background: 'linear-gradient(135deg, var(--cyan-500) 0%, var(--cyan-700) 100%)',
              margin: -1, padding: '20px 24px', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12
            }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="white" />
              </div>
              <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>{editing ? 'Edit Kontrak' : 'Tambah Kontrak'}</h3>
            </div>
            <div className="modal-body">
              <div style={{
                background: 'var(--accent-tint-bg)', borderRadius: 'var(--radius)',
                border: '1px solid var(--cyan-100)', overflow: 'hidden',
              }}>
                {/* Form Section Header */}
                <div style={{
                  padding: '12px 16px', borderBottom: '1px solid var(--cyan-100)',
                  background: 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.5px', color: 'var(--cyan-700)',
                  textAlign: 'center',
                }}>
                  <FileText size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Informasi Kontrak
                </div>

                {/* Grid 2 Kolom */}
                <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                  {/* Kolom Kiri */}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Karyawan <span style={{color:'var(--danger)'}}>*</span></label>
                    <select value={form.karyawan_id} onChange={e => setForm({...form, karyawan_id: parseInt(e.target.value)})} required>
                      <option value="">Pilih Karyawan</option>
                      {karyawan.map(k => <option key={k.id} value={k.id}>{k.nama || k.full_name || k.nik || 'Karyawan #' + k.id}</option>)}
                    </select>
                  </div>
                  {/* Kolom Kanan */}
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Jenis Kontrak</label>
                    <select value={form.jenis_kontrak} onChange={e => setForm({...form, jenis_kontrak: e.target.value})}>
                      {JENIS_KONTRAK.map(j => <option key={j} value={j}>{j}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Mulai <span style={{color:'var(--danger)'}}>*</span></label>
                    <input type="date" value={form.tgl_mulai} onChange={e => setForm({...form, tgl_mulai: e.target.value})} required />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Tanggal Selesai</label>
                    <input type="date" value={form.tgl_selesai} onChange={e => setForm({...form, tgl_selesai: e.target.value})} />
                  </div>
                  {editing && (
                    <div className="form-group" style={{ margin: 0 }}>
                      <label>Status</label>
                      <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                        {STATUS_KONTRAK.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="form-group" style={{ margin: 0, gridColumn: editing ? 'auto' : '1 / -1' }}>
                    <label>No. Kontrak</label>
                    <input type="text" value={form.no_kontrak} onChange={e => setForm({...form, no_kontrak: e.target.value})} placeholder="Contoh: KTR-001/2026" />
                  </div>
                </div>

                {/* Full Width: Keterangan */}
                <div style={{ padding: '0 16px 14px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Keterangan</label>
                    <textarea value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} rows={2} placeholder="Opsional — catatan tambahan..." style={{ fontSize: '0.82rem' }} />
                  </div>
                </div>

                {/* Full Width: File Upload */}
                <div style={{
                  borderTop: '1px dashed var(--cyan-200)', background: 'rgba(255,255,255,0.4)',
                  padding: '14px 16px',
                }}>
                  <div className="file-upload-wrapper" style={{ margin: 0 }}>
                    <label style={{
                      fontSize: '0.75rem', fontWeight: 600, marginBottom: 8,
                      color: 'var(--gray-600)', display: 'block',
                    }}>
                      <Upload size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      File Kontrak (PDF/Word/Foto)
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <input type="file" id="kontrak-file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={e => { setFileData(e.target.files[0]); setFileName(e.target.files[0]?.name || ''); }}
                        style={{ display: 'none' }} />
                      <button type="button" className="btn btn-ghost"
                        onClick={() => document.getElementById('kontrak-file').click()}
                        style={{ fontSize: '0.8rem', padding: '6px 14px' }}>
                        <Upload size={14} /> {fileName || 'Pilih File'}
                      </button>
                      {fileKontrak && !fileData && (
                        <a href={getFileUrl(fileKontrak)} target="_blank" rel="noopener noreferrer"
                          className="btn btn-sm btn-ghost"
                          style={{ fontSize: '0.78rem', color: 'var(--cyan-600)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Download size={13} /> Lihat File
                        </a>
                      )}
                      {fileUploading && <span style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Mengupload...</span>}
                      {fileName && !fileUploading && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--success)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          ✅ {fileName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {fileUploading && <span style={{flex:1,fontSize:'0.8rem',color:'var(--gray-400)'}}>Mengupload file...</span>}
              <button className="btn btn-ghost" onClick={() => { setShowModal(false); setFileData(null); }} disabled={fileUploading}>Batal</button>
              <button className="btn btn-primary" onClick={handleSubmit}>{editing ? 'Simpan' : 'Tambah'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Dokumen Modal */}
      {previewItem && previewItem.file_kontrak && (
        <div className="modal-overlay" onClick={() => setPreviewItem(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ maxWidth: 800, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: '1px solid var(--gray-100)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: 'linear-gradient(135deg, var(--cyan-500), var(--cyan-700))',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FileText size={16} color="#fff" />
                </div>
                <div>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0, color: 'var(--gray-600)' }}>
                    Dokumen Kontrak — {previewItem.nama || previewItem.nama_karyawan || '-'}
                  </h3>
                  <small style={{ color: 'var(--gray-400)' }}>
                    {getFileName(previewItem.file_kontrak)} · {previewItem.no_kontrak ? `No. ${previewItem.no_kontrak}` : ''}
                  </small>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <a href={getFileUrl(previewItem.file_kontrak)}
                   target="_blank" rel="noopener noreferrer"
                   className="btn btn-ghost" title="Buka di tab baru"
                   style={{ padding: '8px 12px', fontSize: '0.78rem' }}>
                  <ExternalLink size={14} /> Buka
                </a>
                <button className="btn-icon" onClick={() => setPreviewItem(null)} title="Tutup">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 0, background: '#f8f9fe' }}>
              {(() => {
                const url = getFileUrl(previewItem.file_kontrak);
                const ext = previewItem.file_kontrak.split('.').pop().toLowerCase();
                const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                const isPdf = ext === 'pdf';

                if (isImage) {
                  return (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <img src={url} alt="Dokumen Kontrak"
                        style={{
                          maxWidth: '100%', maxHeight: '70vh',
                          borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        }}
                        onError={e => {
                          e.target.style.display = 'none';
                          const fb = document.getElementById(`img-err-${previewItem.id}`);
                          if (fb) fb.style.display = 'block';
                        }}
                      />
                      <div id={`img-err-${previewItem.id}`} style={{ display: 'none', padding: '40px 20px', color: 'var(--gray-500)' }}>
                        <FileText size={48} style={{ color: 'var(--gray-300)', marginBottom: 12 }} />
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>Gagal memuat gambar</p>
                      </div>
                    </div>
                  );
                }

                if (isPdf) {
                  return (
                    <iframe
                      src={url}
                      title="Preview PDF"
                      style={{ width: '100%', height: '75vh', border: 'none' }}
                      sandbox="allow-scripts allow-same-origin"
                    />
                  );
                }

                // Word docs and other files
                return (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <FileText size={64} style={{ color: 'var(--gray-300)', marginBottom: 16 }} />
                    <h4 style={{ color: 'var(--gray-500)', margin: '0 0 8px', fontSize: '1rem' }}>
                      File {ext.toUpperCase()}
                    </h4>
                    <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: 20 }}>
                      Preview tidak tersedia untuk file {ext.toUpperCase()}. Silakan buka file langsung.
                    </p>
                    <a href={url} target="_blank" rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Download size={16} /> Buka File {ext.toUpperCase()}
                    </a>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
